import { useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "../../api/client";
import { AppDialog } from "../../components/AppDialog";
import { AiCombo } from "../../components/AiCombo";
import { useNotify } from "../../components/notifications";
import { useConfirm } from "../../components/confirm";
import { QC_KIND, qcKindOf, missingIn, isRetired, type QcParamRow } from "./testCodeQc";

/**
 * Copy Parameters ▾ → **Within this test** (prototype v12).
 *
 * Two operations that stay inside one test, as opposed to the "From another
 * test" group above them which pulls rows across from a different test:
 *
 *   • **Copy QC parameters from same test** — the Sample Parameters out to the
 *     ticked QC types. A parameter the QC type already carries is skipped, so
 *     running it twice changes nothing.
 *   • **Copy QC limits between QC types** — one QC type's limit and rounding
 *     values onto the ticked others (LCS → LCS Digested). Existing rows are
 *     OVERWRITTEN here, because making them agree is the whole point.
 *
 * Both post to the API; nothing is computed in the browser.
 */

type QcType = {
  id: string;
  qcType?: string | null;
  qcRole?: string | null;
  qcRootRole?: string | null;
};

/** The QC types linked to this test — both dialogs tick from the same list. */
function useQcTypes(base: string, open: boolean) {
  const [rows, setRows] = useState<QcType[]>([]);
  useEffect(() => {
    if (!open) return;
    let alive = true;
    void (async () => {
      try {
        const { data } = await api.get(`${base}/qc-types`);
        if (alive) setRows(data?.data ?? []);
      } catch {
        if (alive) setRows([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [base, open]);
  return rows;
}

/** This test's Sample Parameters, and the parameters each QC type already
 *  carries. Feeds "Sample Parameters (N)", the per-row "N params now" chip and
 *  the counted preview - all of which the prototype computes from the same two
 *  collections (v12 W.params / W.qcParams). */
function useParams(base: string, open: boolean) {
  const [sample, setSample] = useState<{ key: string }[]>([]);
  const [byType, setByType] = useState<Map<string, Set<string>>>(new Map());
  const [qcRows, setQcRows] = useState<QcParamRow[]>([]);
  const [retired, setRetired] = useState<Set<string>>(new Set());
  const [name, setName] = useState("");
  useEffect(() => {
    if (!open) return;
    let alive = true;
    void (async () => {
      try {
        const [sp, qp, tm] = await Promise.all([
          api.get(`${base}/test-code/parameters?kind=Sample`),
          api.get(`${base}/qc-parameters`),
          api.get(base),
        ]);
        if (!alive) return;
        setName(String(tm.data?.data?.testName ?? ""));
        // Keyed on the parameter's Oid, falling back to its name: the same key
        // the copy itself uses, so "already there" means the same thing in the
        // preview as it does in the write.
        const key = (r: { parameterId?: string | null; parameter?: string | null }) =>
          String(r.parameterId ?? r.parameter ?? "");
        const spRows = (sp.data?.data ?? []) as Record<string, unknown>[];
        setSample(spRows.map((r) => ({ key: key(r) })));
        // A retired sample parameter's QC rows are not counted as incomplete -
        // the lab stopped reporting them, so missing limits are expected.
        setRetired(new Set(spRows.filter((r) => isRetired(r)).map((r) => key(r))));
        const qcAll = (qp.data?.data ?? []) as QcParamRow[];
        setQcRows(qcAll);
        const m = new Map<string, Set<string>>();
        for (const r of qcAll) {
          const t = String(r.qcTypeId ?? "");
          if (!t) continue;
          if (!m.has(t)) m.set(t, new Set());
          m.get(t)!.add(key(r as Record<string, unknown>));
        }
        setByType(m);
      } catch {
        if (alive) { setSample([]); setByType(new Map()); setQcRows([]); setRetired(new Set()); setName(""); }
      }
    })();
    return () => { alive = false; };
  }, [base, open]);
  return { sample, byType, qcRows, retired, name };
}

function TickList({
  rows,
  picked,
  onToggle,
  exclude,
  headLabel,
  onToggleAll,
  right,
}: {
  rows: QcType[];
  picked: Set<string>;
  onToggle: (id: string) => void;
  /** The source QC type, which cannot also be a target. */
  exclude?: string;
  /** Caption of the select-all header row. Omitted = no header: the prototype
   *  draws one only on the same-test dialog (v12 line 861). */
  headLabel?: string;
  onToggleAll?: (all: boolean) => void;
  /** Right-aligned chip, e.g. "10 params now". Falls back to the QC role. */
  right?: (r: QcType) => ReactNode;
}) {
  const shown = rows.filter((r) => r.id !== exclude);
  const allOn = shown.length > 0 && shown.every((r) => picked.has(r.id));
  const someOn = shown.some((r) => picked.has(r.id));
  return (
    <div className="tc-setcat-list" style={{ maxHeight: 280 }}>
      {headLabel && shown.length > 0 && (
        <label className="tc-copy-head">
          <input
            type="checkbox"
            checked={allOn}
            ref={(el) => { if (el) el.indeterminate = !allOn && someOn; }}
            onChange={() => onToggleAll?.(!allOn)}
          />
          {headLabel}
        </label>
      )}
      {shown.map((r) => (
        <label key={r.id}>
          <input type="checkbox" checked={picked.has(r.id)} onChange={() => onToggle(r.id)} />
          {r.qcType}
          {right ? (
            <span className="tc-copy-rows">{right(r)}</span>
          ) : r.qcRole ? (
            <span className="tc-copy-rows">{r.qcRole}</span>
          ) : null}
        </label>
      ))}
      {!shown.length && (
        <div className="tc-setcat-empty">
          No QC types on this test — add them in Method Defaults → QC Types first.
        </div>
      )}
    </div>
  );
}

/* ── Copy QC parameters from same test ─────────────────────────────────────── */

export function QcFromSampleDialog({
  open,
  base,
  testName,
  onClose,
  onDone,
}: {
  open: boolean;
  base: string;
  /** Named in the read-only "Test" field, as the prototype names it. */
  testName?: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const rows = useQcTypes(base, open);
  const { sample, byType, name: loadedName } = useParams(base, open);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const notify = useNotify();
  const { confirm } = useConfirm();

  useEffect(() => {
    if (open) setPicked(new Set());
  }, [open]);

  // "N sample parameters -> M QC type(s): X rows will be added, Y already
  // present are kept as they are." The add/skip split is per ticked type, the
  // same loop the prototype runs (v12 line 824) - a parameter the QC type
  // already carries is skipped, which is also what the API does.
  const plan = useMemo(() => {
    let add = 0, skip = 0;
    for (const t of picked) {
      const have = byType.get(t) ?? new Set<string>();
      for (const sp of sample) (have.has(sp.key) ? skip++ : add++);
    }
    return { add, skip };
  }, [picked, byType, sample]);

  if (!open) return null;

  const toggle = (id: string) =>
    setPicked((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const run = async () => {
    if (!picked.size) {
      notify.warning("Tick at least one QC type.");
      return;
    }
    const ok = await confirm({
      title: "Copy QC parameters from same test",
      message: `Add this test's Sample Parameters to ${picked.size} QC type${picked.size === 1 ? "" : "s"}? Parameters a QC type already has are left as they are.`,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/qc-parameters/from-sample`, {
        qcTypeOids: [...picked],
      });
      const n = data?.data?.changed ?? 0;
      notify.success(n === 0 ? "No change — those QC types already have them." : `Added ${n} row${n === 1 ? "" : "s"}.`);
      onDone();
      onClose();
    } catch (e: unknown) {
      notify.error(
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
          "Could not copy the QC parameters.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppDialog
      open={open}
      title="Copy QC parameters from same test"
      width={520}
      onClose={onClose}
      className="dlg-center"
      footer={
        <>
          <button
            className="ca-btn ca-btn-solid"
            onClick={() => void run()}
            disabled={busy || !picked.size || !sample.length || !plan.add}
          >
            Copy
          </button>
          <button className="ca-btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
        </>
      }
    >
      {/* The source half: this test's own Sample Parameters. Read-only - the
          dialog copies FROM this test by definition, so there is nothing to
          choose (prototype v12 line 860). */}
      <div className="tc-copy-h">Copy Test Parameter From</div>
      <div className="tc-copy-grid tc-guide-grid">
        <label className="tc-copy-lbl">Test<span className="req">*</span></label>
        <input
          /* "readonly" is the app-wide cue: the base .sd-input is the YELLOW
             editable tint and .readonly overrides it to grey. The field is never
             typed in, so the yellow was claiming it was editable (owner report
             2026-10-15). */
          className="sd-input readonly"
          readOnly
          value={`${testName || loadedName || "This test"} — Sample Parameters (${sample.length})`}
        />
      </div>

      <div className="tc-copy-h" style={{ marginTop: 12 }}>Copy Parameter To</div>
      {rows.length ? (
        <TickList
          rows={rows}
          picked={picked}
          onToggle={toggle}
          headLabel="QCType"
          onToggleAll={(all) => setPicked(all ? new Set(rows.map((r) => r.id)) : new Set())}
          right={(r) => `${(byType.get(r.id) ?? new Set()).size} params now`}
        />
      ) : (
        <div className="tc-copy-note tc-copy-note--warn">This test has no QC types yet.</div>
      )}

      {/* Nothing until a QC type is ticked - the prototype shows "Tick at least
          one QC type." there, but the owner had that prompt removed and the
          disabled Copy button already carries it (2026-10-15). The run() guard
          keeps the wording. */}
      {picked.size > 0 && (
        <div className="tc-copy-note">
          {sample.length} sample parameters → {picked.size} QC type(s):{" "}
          <b>{plan.add} rows will be added</b>
          {plan.skip ? `, ${plan.skip} already present are kept as they are.` : "."}
        </div>
      )}
    </AppDialog>
  );
}

/* ── Copy QC limits between QC types ───────────────────────────────────────── */

export function QcLimitsBetweenDialog({
  open,
  base,
  onClose,
  onDone,
}: {
  open: boolean;
  base: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const rows = useQcTypes(base, open);
  const { byType, qcRows, retired } = useParams(base, open);
  const [from, setFrom] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const notify = useNotify();
  const { confirm } = useConfirm();

  useEffect(() => {
    if (open) {
      setFrom("");
      setPicked(new Set());
    }
  }, [open]);

  const fromRow = rows.find((r) => r.id === from);
  // Only QC types of the SAME KIND are offered: an LCS's limits mean nothing on
  // a Duplicate, because each kind is checked against different limit fields.
  // ⚠ KIND, not role - Spike and Standard are one kind, so an LCS source
  // correctly offers a CCV (prototype v12 line 856; a qcRole match would hide
  // it, which is what this did until 2026-10-15).
  const fromKind = fromRow ? qcKindOf(fromRow) : null;
  const targets = rows.filter((r) => r.id !== from && (!fromKind || qcKindOf(r) === fromKind));

  // "N missing" / "complete", the prototype's missingIn (v12 line 636): across
  // the QC type's rows, how many required limit fields are still empty. Rows of
  // a retired sample parameter are not counted.
  const missingFor = (qcTypeId: string): number => {
    const t = rows.find((r) => r.id === qcTypeId);
    if (!t) return 0;
    return missingIn(
      qcRows.filter((r) => String(r.qcTypeId ?? "") === qcTypeId),
      qcKindOf(t),
      (r) => retired.has(String(r.parameterId ?? r.parameter ?? "")),
    );
  };

  // Rows that will actually change: a target parameter the SOURCE also carries
  // is the one whose values get replaced (prototype v12 line 827). Parameters
  // the target lacks are added on top of that.
  // ⚠ Every hook stays ABOVE the early return below - a useMemo under it runs
  // only while the dialog is open, so React counts a different number of hooks
  // on each render and throws instead of opening the dialog.
  const willUpdate = useMemo(() => {
    const src = byType.get(from) ?? new Set<string>();
    let n = 0;
    for (const t of picked) for (const k of byType.get(t) ?? []) if (src.has(k)) n++;
    return n;
  }, [byType, from, picked]);

  if (!open) return null;

  const toggle = (id: string) =>
    setPicked((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const run = async () => {
    if (!from) {
      notify.warning("Choose the QC type to copy limits from.");
      return;
    }
    const sel = [...picked].filter((id) => targets.some((t) => t.id === id));
    if (!sel.length) {
      notify.warning("Tick at least one QC type to copy to.");
      return;
    }
    const ok = await confirm({
      title: "Copy QC limits between QC types",
      tone: "danger",
      message: `Copy ${fromRow?.qcType}'s limits onto ${sel.length} QC type${sel.length === 1 ? "" : "s"}? Values already on those rows are REPLACED.`,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/qc-parameters/limits-between`, {
        fromQcTypeOid: from,
        toQcTypeOids: sel,
      });
      const n = data?.data?.changed ?? 0;
      notify.success(n === 0 ? "No change." : `Updated ${n} row${n === 1 ? "" : "s"}.`);
      onDone();
      onClose();
    } catch (e: unknown) {
      notify.error(
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
          "Could not copy the QC limits.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppDialog
      open={open}
      title="Copy QC limits between QC types"
      width={520}
      onClose={onClose}
      className="dlg-center"
      footer={
        <>
          <button
            className="ca-btn ca-btn-solid"
            onClick={() => void run()}
            disabled={busy || !from || !picked.size}
          >
            Copy
          </button>
          <button className="ca-btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
        </>
      }
    >
      <div className="tc-copy-grid tc-guide-grid">
        <label className="tc-copy-lbl">Copy limits from<span className="req">*</span></label>
        <AiCombo
          value={from || null}
          options={rows.map((r) => ({ value: r.id, label: `${r.qcType}${r.qcRole ? ` (${r.qcRole})` : ""}` }))}
          placeholder="— Select —"
          onChange={(v) => {
            setFrom(v ?? "");
            setPicked(new Set());
          }}
        />
      </div>

      <div className="tc-copy-h" style={{ marginTop: 12 }}>
        Copy to (same role: {fromKind ? QC_KIND[fromKind].label : "—"})
      </div>
      <TickList
        rows={targets}
        picked={picked}
        onToggle={toggle}
        exclude={from}
        right={(r) => {
          const n = missingFor(r.id);
          return n ? `${n} missing` : "complete";
        }}
      />

      {/* No prompt banners - Copy is disabled until a source is chosen and a
          target ticked, which says the same thing (owner request 2026-10-15).
          An EMPTY target list is still explained, since nothing else on screen
          would account for it. */}
      {from && !targets.length ? (
        <div className="tc-copy-note tc-copy-note--warn">
          No other QC type on this test has the same role.
        </div>
      ) : from && picked.size ? (
        <div className="tc-copy-note">
          Limits and rounding from <b>{fromRow?.qcType}</b> → {picked.size} QC type(s):{" "}
          <b>{willUpdate} parameter rows will be updated</b> (existing values are
          replaced). Parameters missing on a target are added.
        </div>
      ) : null}


    </AppDialog>
  );
}
